/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Button, Col, Grid, Modal, Row, Typography } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { CheckCircleIcon } from '../Icons/CheckCircleIcon';
import TextAreaWithCopyButton from '../Forms/Fields/TextAreaWithCopyButton';

// Misc imports
import { getDeploymentLibraryScript, getDeploymentSnippet } from '../../utils/misc/getDeploymentSnippet';

// Constant declarations
const { useBreakpoint } = Grid;

const WebchatDeploymentSnippetModal = ({
  isModalOpen=false,
  onModalClose=() => {},
  loader=false,
  deploymentToCopySnippet={}
}) => {
  const screens = useBreakpoint();
  const isMobileSize = screens.md;
  const [isCopySuccessModalOpen, setIsCopySuccessModalOpen] = useState(false);
  const { t } = useTranslation();

  const modalProps = useMemo(() => {
    return {
      mainModalWidth: !isMobileSize ? '100%' : '45%',
      successModalWidth: !isMobileSize ? '70%' : '20%'
    };
  }, [isMobileSize]);

  const libraryScript = getDeploymentLibraryScript().trim();
  const script = getDeploymentSnippet({ deploymentId: deploymentToCopySnippet?.id }).trim();

  return (
    <Modal
      destroyOnClose
      title={
        <Fragment>
          <Typography.Title level={4} className="mb-0">
            {t('JavaScript Snippet')}
          </Typography.Title>
          <Typography.Text className="text-muted">
            {t('Deploy your Agent')}
          </Typography.Text>
        </Fragment>
      }
      width={modalProps.mainModalWidth}
      open={isModalOpen}
      onCancel={onModalClose}
      onOk={onModalClose}
      centered
      footer={[
        <Button
          loading={loader}
          type="primary"
        >
          {t('OK')}
        </Button>
      ]}
    >
      <Row className="mb-5" gutter={24}>
        <Col span={24}>
          <Typography.Text className="text-muted">
            {t('Insert this code at the <HEAD> section on the webpage where the agent is to be deployed.')}
          </Typography.Text>
        </Col>

        <Col span={24}>
          <TextAreaWithCopyButton
            readOnly
            autoSize={{ minRows: 2, maxRows: 32 }}
            className="text-code"
            value={libraryScript}
            spellCheck={false}
          />
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={24}>
          <Typography.Text className="text-muted">
            {t('Insert this code at the end of the <BODY> section on the webpage where the agent is to be deployed.')}
          </Typography.Text>
        </Col>

        <Col span={24}>
          <TextAreaWithCopyButton
            readOnly
            autoSize={{ minRows: 2, maxRows: 32 }}
            className="text-code"
            value={script}
            spellCheck={false}
          />
        </Col>
      </Row>
      <Modal
        destroyOnClose
        width={modalProps.successModalWidth}
        centered
        title=""
        footer={false}
        closable={false}
        onCancel={() => setIsCopySuccessModalOpen(false)}
        open={isCopySuccessModalOpen}
      >
        <Row gutter={24} className="justify-content-center text-center">
          <Col span={24}>
            <CheckCircleIcon className="fs-xxl text-success"/>
          </Col>
          <Col span={24}>
            <CopyOutlined className="fs-lg mr-3"/>
            <Typography.Text className="fs-xl fw-bold">
              {t('Code Copied!')}
            </Typography.Text>
          </Col>
        </Row>
      </Modal>
    </Modal>
  );
};

WebchatDeploymentSnippetModal.propTypes = {
  isModalOpen: PropTypes.bool,
  onModalClose: PropTypes.func,
  loader: PropTypes.bool,
  deploymentToCopySnippet: PropTypes.object
};

export default WebchatDeploymentSnippetModal;
